import { useTranslation } from "react-i18next";
import { Button } from "@carbon/react";
import { OfferResponse, ORDER_TYPES } from "../../types";
import "./bid.scss";

export type JointSellerOfferActionsProps = {
  offers: OfferResponse[];
  isOrderOpen: boolean;
  isDeclined: boolean;
  canShowDeclineToggle: boolean;
  editingOffers: Record<string, { price: string; currency: string }>;
  updatingOfferId: string | null;
  errorsCount: number;
  offersToUpdateCount: number;
  isUpdating: boolean;
  isDeclining: boolean;
  onOpenDecline: () => void;
  onInverseUpdate: () => void;
  onCancelEdit: (offerId: string) => void;
  onSaveEdit: (offerId: string) => void;
};

/**
 * The JOINT seller's action bars (Task 6) — the "split into … actions" piece. Renders, per the
 * pre-Carbon container: the inverse-joint submit bar (decline + submit-bids), the normal-joint
 * decline bar (only while no line is being edited), and the per-line save/cancel bar for every
 * line currently in edit mode. All gating is computed by the caller.
 */
const JointSellerOfferActions = ({
  offers,
  isOrderOpen,
  isDeclined,
  editingOffers,
  updatingOfferId,
  errorsCount,
  offersToUpdateCount,
  isUpdating,
  onInverseUpdate,
  onCancelEdit,
  onSaveEdit,
}: JointSellerOfferActionsProps) => {
  const { t } = useTranslation();

  const hasInverseJointOpen =
    isOrderOpen &&
    offers?.some((offer) => offer?.order?.type === ORDER_TYPES.INVERSE_JOINT);
  const isEditingAny = Object.keys(editingOffers).length > 0;

  const updateDisabled =
    errorsCount > 0 ||
    offersToUpdateCount === 0 ||
    isUpdating ||
    !isOrderOpen ||
    isDeclined;

  return (
    <>
      {/* "Will not quote" (decline the whole joint order) now lives in the header beside Chat — see
          SellerOfferHeader. These bars only carry the price-editing actions. */}
      {hasInverseJointOpen && (
        <div className="seller-offer__actions">
          <Button size="sm" disabled={updateDisabled} onClick={onInverseUpdate}>
            {isUpdating ? t("cta.loading") : t("cta.update")}
          </Button>
        </div>
      )}

      {isEditingAny && (
        <div className="seller-offer__actions">
          {offers?.map((offer) => {
            if (!editingOffers[offer?.id]) return null;
            const isThisUpdating = updatingOfferId === offer?.id;
            const canUpdate = isOrderOpen && !offer?.selected;
            const saveDisabled =
              !editingOffers[offer?.id]?.price ||
              parseFloat(editingOffers[offer?.id]?.price || "0") <= 0 ||
              !editingOffers[offer?.id]?.currency ||
              isThisUpdating ||
              !canUpdate ||
              isDeclined;

            return (
              <div key={offer?.id} className="seller-offer__actions-pair">
                <Button
                  kind="secondary"
                  size="sm"
                  disabled={isThisUpdating}
                  onClick={() => onCancelEdit(offer?.id)}
                >
                  {t("cta.cancel")}
                </Button>
                <Button
                  size="sm"
                  disabled={saveDisabled}
                  onClick={() => onSaveEdit(offer?.id)}
                >
                  {isThisUpdating ? t("cta.loading") : t("cta.edit")}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default JointSellerOfferActions;
